import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "../../../components/header";
import { Loading } from "../../../components/loading";
import { CardHeader } from "../../../components/cards";
import { SendIcon } from "../../../components/icons";
import { useOrder } from "../hooks";
import { OrderLineForm, OrderLinesList } from "../../order-lines/components";
import { SignaturesList } from "../../signatures/components";
import { SignatureLinkForm, SignatureLinksList } from "../../signature-links/components";
import { EmailLogsList } from "../../email-logs/components";
import {
  orderStatusLabel,
  paymentStatusLabel,
  deliveryStatusLabel,
} from "../../../helpers/enum-labels";

type IOrderParams = {
  id: string;
};

export const Order: React.FC = () => {
  const { id } = useParams<IOrderParams>();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = React.useState(0);

  useEffect(() => {
    if (!id) {
      navigate("/");
    }
  }, []);

  const { data, loading, refetch } = useOrder(+id!);

  if (loading) return <Loading />;

  const order = data?.order?.result;

  return (
    <>
      <Header
        title="Commande"
        subtitle={order?.number ?? `#${id}`}
        buttons={[
          {
            actionText: "Bon de commande",
            bgColor: "green",
            textColor: "white",
            link: `/order/${id}/bon-de-commande`,
            icon: <SendIcon />,
          },
          {
            actionText: "Modifier",
            bgColor: "indigo",
            textColor: "white",
            link: `/order/${id}/update`,
            icon: <SendIcon />,
          },
        ]}
      />
      <div className="main-container">
        <div className="card">
          <CardHeader title="Informations générales" />
          <div className="flex">
            <div className="w-1/2 mr-1">
              <div className="w-full mt-3">
                <p className="label">Statut</p>
                <input className="input w-full" value={orderStatusLabel(order?.status)} disabled />
              </div>
            </div>
            <div className="w-1/2 ml-1">
              <div className="w-full mt-3">
                <p className="label">Paiement</p>
                <input className="input w-full" value={paymentStatusLabel(order?.paymentStatus)} disabled />
              </div>
            </div>
          </div>
          <div className="flex">
            <div className="w-1/2 mr-1">
              <div className="w-full mt-3">
                <p className="label">Livraison</p>
                <input className="input w-full" value={deliveryStatusLabel(order?.deliveryStatus)} disabled />
              </div>
            </div>
            <div className="w-1/2 ml-1">
              <div className="w-full mt-3">
                <p className="label">Client</p>
                <input className="input w-full" value={order?.customer?.name} disabled />
              </div>
            </div>
          </div>
          <div className="flex">
            <div className="w-1/2 mr-1">
              <div className="w-full mt-3">
                <p className="label">Total HT</p>
                <input
                  className="input w-full"
                  value={((order?.totalHtCents ?? 0) / 100).toFixed(2) + " €"}
                  disabled
                />
              </div>
            </div>
            <div className="w-1/2 ml-1">
              <div className="w-full mt-3">
                <p className="label">Total TTC</p>
                <input
                  className="input w-full"
                  value={((order?.totalTtcCents ?? 0) / 100).toFixed(2) + " €"}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <CardHeader title="Ajouter une ligne" />
          <OrderLineForm
            orderId={+id!}
            customerId={order?.customer?.id}
            onCompleted={refetch}
          />
        </div>

        <div className="card mt-4">
          <CardHeader title="Lignes de commande" />
          <OrderLinesList orderId={+id!} />
        </div>

        <div className="section mt-4">
          <div className="left">
            <div className="card">
              <CardHeader title="Signatures" />
              <SignaturesList orderId={+id!} />
            </div>
          </div>

          <div className="right">
            <div className="card">
              <CardHeader title="Liens de signature" />
              <SignatureLinkForm
                orderId={+id!}
                onCompleted={() => setRefreshKey((k) => k + 1)}
              />
              <div className="mt-4">
                <SignatureLinksList key={refreshKey} orderId={+id!} />
              </div>
            </div>
          </div>
        </div>

        <div className="card mt-4">
          <CardHeader title="Emails envoyés" />
          <EmailLogsList orderId={+id!} />
        </div>
      </div>
    </>
  );
};
